import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, ReactNode } from 'react';

export type ExperienceMode = 'simple' | 'pro';

const STORAGE_KEY = 'mmec_experience_mode';
const URL_PARAM = 'experience';

interface ExperienceModeContextValue {
  mode: ExperienceMode;
  setMode: (mode: ExperienceMode) => void;
}

const ExperienceModeContext = createContext<ExperienceModeContextValue | undefined>(undefined);

function readInitialMode(): ExperienceMode {
  if (typeof window === 'undefined') return 'pro';
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get(URL_PARAM);
    if (q === 'simple' || q === 'pro') return q;
  } catch {
    /* ignore */
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'simple' || stored === 'pro') return stored;
  return 'pro';
}

export const ExperienceModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ExperienceMode>(readInitialMode);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(URL_PARAM, mode);
      window.history.replaceState({}, '', url.toString());
    } catch {
      /* ignore */
    }
  }, [mode]);

  const setMode = useCallback((next: ExperienceMode) => {
    setModeState(next);
  }, []);

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return <ExperienceModeContext.Provider value={value}>{children}</ExperienceModeContext.Provider>;
};

export function useExperienceMode(): ExperienceModeContextValue {
  const ctx = useContext(ExperienceModeContext);
  if (!ctx) {
    throw new Error('useExperienceMode must be used within ExperienceModeProvider');
  }
  return ctx;
}
